import React, { useState, useCallback } from 'react';
import { api } from '../App';
import toast from 'react-hot-toast';
import { sanitizeSource } from '../utils/whitelabel';

const MARKETS = [
  { label: 'Phoenix, AZ', value: 'Phoenix' },
  { label: 'Sacramento, CA', value: 'Sacramento' },
  { label: 'Houston, TX', value: 'Houston' },
  { label: 'Dallas, TX', value: 'Dallas' },
  { label: 'Austin, TX', value: 'Austin' },
  { label: 'Miami, FL', value: 'Miami' },
  { label: 'Denver, CO', value: 'Denver' },
  { label: 'Las Vegas, NV', value: 'Las Vegas' },
  { label: 'Charlotte, NC', value: 'Charlotte' },
  { label: 'Atlanta, GA', value: 'Atlanta' },
  { label: 'Nashville, TN', value: 'Nashville' },
  { label: 'Salt Lake City, UT', value: 'Salt Lake City' },
];

function ConfidenceMeter({ value, label }) {
  const color = value >= 75 ? '#10b981' : value >= 50 ? '#f59e0b' : value >= 25 ? '#f97316' : '#ef4444';
  return (
    <div data-testid={`confidence-meter-${label}`} className="flex items-center gap-2">
      <div className="w-24 h-2 rounded-full bg-white/10 overflow-hidden">
        <div className="h-full rounded-full transition-all duration-1000" style={{ width: `${value}%`, background: color }} />
      </div>
      <span className="text-xs font-mono" style={{ color }}>{value}%</span>
    </div>
  );
}

function StatusBadge({ verified, label }) {
  return (
    <span data-testid={`badge-${label}`} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${verified ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 'bg-amber-500/15 text-amber-400 border border-amber-500/20'}`}>
      <i className={`fa-solid ${verified ? 'fa-circle-check' : 'fa-circle-exclamation'} text-[8px]`} />
      {verified ? 'Verified' : 'Unverified'}
    </span>
  );
}

function MethodTag({ method }) {
  const styles = {
    free: { bg: 'bg-cyan-500/15', text: 'text-cyan-400', border: 'border-cyan-500/30', icon: 'fa-bolt', label: 'FREE' },
    paid: { bg: 'bg-violet-500/15', text: 'text-violet-400', border: 'border-violet-500/30', icon: 'fa-crown', label: 'PREMIUM' },
    skipped: { bg: 'bg-zinc-500/15', text: 'text-zinc-400', border: 'border-zinc-500/30', icon: 'fa-forward', label: 'SKIPPED' },
  };
  const s = styles[method] || styles.skipped;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[10px] font-black uppercase tracking-widest ${s.bg} ${s.text} border ${s.border}`}>
      <i className={`fa-solid ${s.icon}`} /> {s.label}
    </span>
  );
}

function DataSourcePill({ source }) {
  const label = sanitizeSource(source);
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-white/5 text-[10px] text-zinc-400 border border-white/5">
      <i className="fa-solid fa-shield-halved text-[8px] text-zinc-500" /> {label}
    </span>
  );
}

function LeadCard({ result, index, animDelay }) {
  const [expanded, setExpanded] = useState(false);
  const lead = result.lead || {};
  const enrich = result.enrichment || {};
  const method = result.method;

  if (method === 'skipped') {
    return (
      <div data-testid={`lead-card-${index}`} className="rounded-xl border border-white/5 bg-white/[0.02] p-5 opacity-50" style={{ animationDelay: `${animDelay}ms` }}>
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-semibold text-zinc-400">{lead.name || 'Unknown'}</span>
          <MethodTag method="skipped" />
        </div>
        <p className="text-xs text-zinc-500">{result.reason}</p>
      </div>
    );
  }

  return (
    <div data-testid={`lead-card-${index}`} className="rounded-xl border border-white/[0.08] bg-gradient-to-br from-white/[0.03] to-transparent p-0 overflow-hidden animate-fadeIn" style={{ animationDelay: `${animDelay}ms` }}>
      {/* Header */}
      <div className="px-5 pt-5 pb-3 flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <MethodTag method={method} />
            <span className="text-[10px] text-zinc-500 font-mono">#{index}</span>
          </div>
          <h3 className="text-lg font-bold text-white tracking-tight">{lead.owner_name || lead.name}</h3>
          <p className="text-xs text-zinc-400 mt-0.5">{lead.address}</p>
          <p className="text-[11px] text-zinc-500">{lead.city}, {lead.state} {lead.zip}</p>
        </div>
        <div className="text-right">
          <div className="text-2xl font-black text-white">{enrich.accuracy_score || 0}<span className="text-sm text-zinc-500 font-normal">%</span></div>
          <div className="text-[10px] text-zinc-500 uppercase tracking-wider">Accuracy</div>
        </div>
      </div>

      {/* Quick Stats Row */}
      <div className="px-5 py-3 grid grid-cols-4 gap-3 border-t border-white/5">
        <div>
          <div className="text-[10px] text-zinc-500 uppercase mb-0.5">Property</div>
          <div className="text-sm font-bold text-white">${(lead.property_value || 0).toLocaleString()}</div>
        </div>
        <div>
          <div className="text-[10px] text-zinc-500 uppercase mb-0.5">Solar</div>
          <div className="text-sm font-bold" style={{ color: lead.solar_potential === 'High' ? '#10b981' : lead.solar_potential === 'Medium' ? '#f59e0b' : '#ef4444' }}>{lead.solar_potential}</div>
        </div>
        <div>
          <div className="text-[10px] text-zinc-500 uppercase mb-0.5">System</div>
          <div className="text-sm font-bold text-white">{lead.recommended_system_kw}kW</div>
        </div>
        <div>
          <div className="text-[10px] text-zinc-500 uppercase mb-0.5">Score</div>
          <div className="text-sm font-bold text-white">{lead.score}/100</div>
        </div>
      </div>

      {/* Contact Intel */}
      <div className="px-5 py-3 border-t border-white/5 space-y-2.5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <i className="fa-solid fa-envelope text-xs text-cyan-400/70 w-4" />
            <span className="text-sm text-white font-mono">{enrich.top_email || 'Not found'}</span>
          </div>
          <ConfidenceMeter value={enrich.email_confidence || 0} label="email" />
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <i className="fa-solid fa-phone text-xs text-emerald-400/70 w-4" />
            <span className="text-sm text-white font-mono">{enrich.phone || 'Area code only'}</span>
          </div>
          <ConfidenceMeter value={enrich.phone_confidence || 0} label="phone" />
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <i className="fa-solid fa-user-check text-xs text-violet-400/70 w-4" />
            <span className="text-sm text-white">Name</span>
          </div>
          <div className="flex items-center gap-2">
            <StatusBadge verified={enrich.name_verified} label="name" />
            <ConfidenceMeter value={enrich.name_confidence || 0} label="name" />
          </div>
        </div>

        {/* Twilio Premium Data */}
        {method === 'paid' && enrich.twilio_verified !== null && enrich.twilio_verified !== undefined && (
          <div className="mt-2 p-2.5 rounded-lg bg-violet-500/10 border border-violet-500/20">
            <div className="text-[10px] font-bold text-violet-400 uppercase tracking-wider mb-1.5">
              <i className="fa-solid fa-crown mr-1" /> Premium Carrier Intel
            </div>
            <div className="grid grid-cols-2 gap-1 text-xs">
              <div className="text-zinc-400">Verified: <span className={enrich.twilio_verified ? 'text-emerald-400' : 'text-red-400'}>{enrich.twilio_verified ? 'YES' : 'NO'}</span></div>
              <div className="text-zinc-400">Carrier: <span className="text-white">{enrich.twilio_carrier || 'N/A'}</span></div>
              <div className="text-zinc-400">Type: <span className="text-white">{enrich.twilio_line_type || 'N/A'}</span></div>
              <div className="text-zinc-400">Caller: <span className="text-white">{enrich.twilio_caller_name || 'N/A'}</span></div>
            </div>
          </div>
        )}
      </div>

      {/* Skip Trace CTA */}
      {result.lead_id && (
        <div className="px-5 py-2.5 border-t border-white/5 flex items-center justify-between">
          <span className="text-[10px] text-zinc-500">Want more data?</span>
          <a href="/skip-trace" data-testid={`skip-trace-cta-${index}`}
            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-[11px] font-bold bg-gradient-to-r from-violet-600/80 to-pink-600/80 text-white hover:from-violet-500 hover:to-pink-500 transition-all">
            <i className="fa-solid fa-magnifying-glass-location text-[9px]" /> Skip Trace ($1.50)
          </a>
        </div>
      )}

      {/* Expand Details */}
      <button data-testid={`expand-lead-${index}`} onClick={() => setExpanded(!expanded)} className="w-full px-5 py-2 border-t border-white/5 flex items-center justify-center gap-1.5 text-[11px] text-zinc-500 hover:text-zinc-300 hover:bg-white/[0.02] transition-all">
        <i className={`fa-solid ${expanded ? 'fa-chevron-up' : 'fa-chevron-down'} text-[8px]`} />
        {expanded ? 'Hide' : 'Show'} Full Intel
      </button>

      {expanded && (
        <div className="px-5 pb-4 space-y-3 border-t border-white/5 bg-white/[0.01]">
          {/* Property Details */}
          <div className="pt-3">
            <div className="text-[10px] font-bold text-zinc-500 uppercase tracking-wider mb-2">Property Intel</div>
            <div className="grid grid-cols-3 gap-2 text-xs">
              <div className="text-zinc-500">Year Built: <span className="text-zinc-300">{lead.year_built}</span></div>
              <div className="text-zinc-500">SqFt: <span className="text-zinc-300">{(lead.sqft || 0).toLocaleString()}</span></div>
              <div className="text-zinc-500">Beds/Baths: <span className="text-zinc-300">{lead.beds || '?'}/{lead.baths || '?'}</span></div>
              <div className="text-zinc-500">Panels: <span className="text-zinc-300">{lead.panel_count}</span></div>
              <div className="text-zinc-500">kWh/yr: <span className="text-zinc-300">{(lead.annual_kwh || 0).toLocaleString()}</span></div>
              <div className="text-zinc-500">Savings: <span className="text-emerald-400">${(lead.estimated_savings || 0).toLocaleString()}/yr</span></div>
              <div className="text-zinc-500">Sunshine: <span className="text-amber-400">{lead.max_sunshine_hours} hrs/yr</span></div>
              <div className="text-zinc-500">Absentee: <span className={lead.is_absentee_owner ? 'text-amber-400' : 'text-zinc-300'}>{lead.is_absentee_owner ? 'YES' : 'No'}</span></div>
              {lead.mailing_address && <div className="col-span-3 text-zinc-500">Mailing: <span className="text-zinc-300">{lead.mailing_address}</span></div>}
            </div>
          </div>

          {/* Email Alternatives */}
          {enrich.top_3_emails?.length > 0 && (
            <div>
              <div className="text-[10px] font-bold text-zinc-500 uppercase tracking-wider mb-1.5">Top Email Candidates</div>
              <div className="flex flex-wrap gap-1">
                {enrich.top_3_emails.map((em, j) => (
                  <span key={j} className="text-[11px] px-2 py-0.5 rounded bg-cyan-500/10 text-cyan-400 border border-cyan-500/15 font-mono">{em}</span>
                ))}
              </div>
            </div>
          )}

          {/* All Phones */}
          {enrich.all_phones?.length > 0 && (
            <div>
              <div className="text-[10px] font-bold text-zinc-500 uppercase tracking-wider mb-1.5">Phone Numbers Found</div>
              <div className="flex flex-wrap gap-1">
                {enrich.all_phones.map((ph, j) => (
                  <span key={j} className="text-[11px] px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/15 font-mono">{ph}</span>
                ))}
              </div>
            </div>
          )}

          {/* Verification Steps */}
          {enrich.verification_steps?.length > 0 && (
            <div>
              <div className="text-[10px] font-bold text-zinc-500 uppercase tracking-wider mb-1.5">Verification Chain</div>
              <div className="space-y-1">
                {enrich.verification_steps.map((step, j) => (
                  <div key={j} className="flex items-start gap-1.5 text-[11px] text-zinc-400">
                    <i className={`fa-solid ${step.includes('VERIFIED') || step.includes('verified') ? 'fa-check text-emerald-400' : step.includes('MISMATCH') ? 'fa-xmark text-red-400' : 'fa-minus text-zinc-500'} mt-0.5 text-[8px] w-3`} />
                    {step}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Data Sources */}
          {enrich.data_sources?.length > 0 && (
            <div>
              <div className="text-[10px] font-bold text-zinc-500 uppercase tracking-wider mb-1.5">Data Sources ({enrich.data_sources.length})</div>
              <div className="flex flex-wrap gap-1">
                {enrich.data_sources.map((src, j) => <DataSourcePill key={j} source={src} />)}
              </div>
            </div>
          )}

          {/* Cross-References */}
          {enrich.cross_references?.length > 0 && (
            <div>
              <div className="text-[10px] font-bold text-zinc-500 uppercase tracking-wider mb-1.5">Cross-References</div>
              <div className="space-y-1">
                {enrich.cross_references.map((xref, j) => (
                  <div key={j} className="flex items-center gap-2 text-[11px]">
                    <StatusBadge verified={xref.verified} label={`xref-${j}`} />
                    <span className="text-zinc-400">{xref.source}: {xref.field}</span>
                    <span className="text-zinc-500 font-mono">({Math.round((xref.match_score || 0) * 100)}% match)</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function EnrichmentLab() {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [selectedMarket, setSelectedMarket] = useState('Phoenix');
  const [progress, setProgress] = useState(0);
  const [progressText, setProgressText] = useState('');
  const [exporting, setExporting] = useState(false);

  const exportLeads = async () => {
    setExporting(true);
    try {
      const res = await api.get('/api/export/leads', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.setAttribute('download', `solar_leads_${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV exported!');
    } catch { toast.error('Export failed'); }
    setExporting(false);
  };

  const runTest = useCallback(async () => {
    setLoading(true);
    setResults(null);
    setProgress(0);

    const steps = [
      { pct: 10, text: 'Connecting to property intelligence network...' },
      { pct: 25, text: 'Scanning real properties with owner data...' },
      { pct: 40, text: 'Analyzing roof & solar potential...' },
      { pct: 55, text: 'Running contact enrichment pipeline...' },
      { pct: 70, text: 'Cross-referencing verified records...' },
      { pct: 85, text: 'Validating email deliverability...' },
      { pct: 95, text: 'Compiling intelligence report...' },
    ];

    let stepIndex = 0;
    const progressInterval = setInterval(() => {
      if (stepIndex < steps.length) {
        setProgress(steps[stepIndex].pct);
        setProgressText(steps[stepIndex].text);
        stepIndex++;
      }
    }, 2500);

    try {
      const res = await api.post('/api/enrich/test-live', { location: selectedMarket, count: 5 });
      clearInterval(progressInterval);
      setProgress(100);
      setProgressText('Complete!');
      setTimeout(() => setResults(res.data), 400);
      toast.success(`${res.data.total_enriched} leads enriched from ${selectedMarket}!`);
    } catch (err) {
      clearInterval(progressInterval);
      toast.error(err.response?.data?.detail || 'Enrichment test failed');
    } finally {
      setLoading(false);
    }
  }, [selectedMarket]);

  return (
    <div data-testid="enrichment-lab-page" className="space-y-6 pb-12">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl border border-white/[0.08] bg-gradient-to-br from-[#0c0c1d] via-[#0f1225] to-[#0c0c1d] p-8">
        <div className="absolute inset-0 opacity-30" style={{ background: 'radial-gradient(ellipse at 30% 50%, rgba(99,102,241,0.15) 0%, transparent 70%), radial-gradient(ellipse at 70% 50%, rgba(6,182,212,0.1) 0%, transparent 70%)' }} />
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-cyan-500 flex items-center justify-center">
              <i className="fa-solid fa-flask text-white text-lg" />
            </div>
            <div>
              <h1 className="text-2xl font-black text-white tracking-tight">Enrichment Lab</h1>
              <p className="text-sm text-zinc-400">Live intelligence test — real data, real results</p>
            </div>
          </div>
          <p className="text-xs text-zinc-500 max-w-xl mt-2">
            Scans 5 real properties, analyzes roof & solar potential, enriches contacts with
            multi-source verified records, and validates carrier data. 2 FREE + 2 PREMIUM + 1 bonus.
          </p>

          {/* Market Selector + Run */}
          <div className="mt-6 flex items-end gap-3 flex-wrap">
            <div>
              <label className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1 block">Target Market</label>
              <select
                data-testid="market-selector"
                value={selectedMarket}
                onChange={(e) => setSelectedMarket(e.target.value)}
                className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-indigo-500 min-w-[200px]"
                disabled={loading}
              >
                {MARKETS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </div>
            <button
              data-testid="run-enrichment-test-btn"
              onClick={runTest}
              disabled={loading}
              className="px-6 py-2 rounded-lg font-bold text-sm text-white bg-gradient-to-r from-indigo-600 to-cyan-600 hover:from-indigo-500 hover:to-cyan-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-indigo-500/20"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <i className="fa-solid fa-spinner animate-spin" /> Running...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <i className="fa-solid fa-rocket" /> Run Live Test
                </span>
              )}
            </button>
          </div>

          {/* Progress Bar */}
          {loading && (
            <div className="mt-4 space-y-1.5">
              <div className="w-full h-1.5 rounded-full bg-white/5 overflow-hidden">
                <div className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-cyan-500 transition-all duration-500" style={{ width: `${progress}%` }} />
              </div>
              <p className="text-xs text-zinc-400 font-mono animate-pulse">{progressText}</p>
            </div>
          )}
        </div>
      </div>

      {/* Results */}
      {results && (
        <div className="space-y-5 animate-fadeIn">
          {/* Summary Bar */}
          <div data-testid="results-summary" className="grid grid-cols-2 md:grid-cols-6 gap-3">
            {[
              { label: 'Scanned', value: results.total_scanned, icon: 'fa-satellite', color: 'text-indigo-400' },
              { label: 'Enriched', value: results.total_enriched, icon: 'fa-wand-magic-sparkles', color: 'text-cyan-400' },
              { label: 'Free', value: results.free_enriched, icon: 'fa-bolt', color: 'text-emerald-400' },
              { label: 'Premium', value: results.paid_enriched, icon: 'fa-crown', color: 'text-violet-400' },
              { label: 'Cost', value: `$${results.total_cost?.toFixed(2)}`, icon: 'fa-receipt', color: 'text-amber-400' },
              { label: 'Credits Left', value: results.remaining_credits?.toFixed(0), icon: 'fa-coins', color: 'text-yellow-400' },
            ].map((s, i) => (
              <div key={i} className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-3 text-center">
                <i className={`fa-solid ${s.icon} ${s.color} text-lg mb-1`} />
                <div className="text-xl font-black text-white">{s.value}</div>
                <div className="text-[10px] text-zinc-500 uppercase tracking-wider">{s.label}</div>
              </div>
            ))}
          </div>

          {/* API Status */}
          <div data-testid="api-status" className="flex flex-wrap gap-2">
            {Object.entries(results.api_status || {}).map(([key, val]) => (
              <span key={key} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider ${val ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-red-500/10 text-red-400 border border-red-500/20'}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${val ? 'bg-emerald-400' : 'bg-red-400'}`} />
                {sanitizeSource(key)}
              </span>
            ))}
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-3">
            <a href="/skip-trace" data-testid="go-to-skip-trace" className="px-4 py-2 rounded-lg text-xs font-bold bg-gradient-to-r from-violet-600 to-pink-600 text-white hover:from-violet-500 hover:to-pink-500 transition-all flex items-center gap-2">
              <i className="fa-solid fa-magnifying-glass-location" /> Skip Trace Leads
            </a>
            <button data-testid="export-btn" onClick={exportLeads} disabled={exporting}
              className="px-4 py-2 rounded-lg text-xs font-bold text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 hover:bg-emerald-500/20 transition-all flex items-center gap-2 disabled:opacity-50">
              <i className={`fa-solid ${exporting ? 'fa-spinner animate-spin' : 'fa-file-csv'}`} /> Export CSV
            </button>
          </div>

          {/* Lead Cards */}
          <div className="space-y-4">
            {results.results?.map((r, i) => (
              <LeadCard key={i} result={r} index={r.index || i + 1} animDelay={i * 150} />
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {!results && !loading && (
        <div className="text-center py-20 opacity-40">
          <i className="fa-solid fa-flask text-5xl text-zinc-600 mb-4" />
          <p className="text-zinc-500 text-sm">Select a market and run the live test to see real enriched leads</p>
        </div>
      )}
    </div>
  );
}
